import { useState, useEffect } from 'react';

/**
 * useScrolled — returns whether the window has scrolled past `threshold` (px).
 * Passive listener for performance.
 */
export function useScrolled(threshold = 24) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > threshold);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  return isScrolled;
}

/**
 * useActiveSection — observes section ids and reports the currently-active one
 * based on intersection with the viewport.
 *
 * @param {string[]} sectionIds      Stable list of section ids to track.
 * @param {boolean}  enabled         Only attach the observer when true (e.g. on the home page).
 * @param {string}   initial         Initial active section id.
 * @returns {string} active section id
 */
export function useActiveSection(sectionIds, enabled, initial) {
  const [activeSection, setActiveSection] = useState(initial ?? (sectionIds?.[0] || ''));

  // Re-join the ids into a stable string so this effect doesn't re-run on
  // array identity changes from callers that pass inline literals.
  const idsKey = (sectionIds || []).join(',');

  useEffect(() => {
    if (!enabled) return undefined;
    const ids = idsKey ? idsKey.split(',') : [];
    if (ids.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [idsKey, enabled]);

  return activeSection;
}
